<template>
  <div v-if="isOpen" class="pin-modal-overlay">
    <div class="pin-modal-content">
      <h3 class="pin-modal-title">{{ isAuthMode ? 'PINコード認証' : 'PINコードの設定' }}</h3>
      
      <div class="pin-modal-body">
        <p class="pin-description" v-if="!isAuthMode">
          クラウド保存するデータを保護するために4桁のPINコードを設定してください。<br>
          このPINコードは<strong>データ復元時に必要となります</strong>。忘れた場合、データを復元できなくなりますのでご注意ください。
        </p>
        <p class="pin-description" v-else>
          クラウドデータにアクセスするためにPINコードを入力してください。
        </p>
        
        <div class="pin-input-container">
          <label for="pin-code">PINコード (4桁の数字)：</label>
          <input 
            type="password" 
            id="pin-code" 
            v-model="pinCode" 
            maxlength="4" 
            pattern="[0-9]*" 
            inputmode="numeric"
            @input="validatePinCode"
            ref="pinInput"
          />
          <div v-if="pinError" class="pin-error">{{ pinError }}</div>
        </div>
        
        <!-- 認証モードでは確認用入力を非表示 -->
        <div class="pin-input-container" v-if="!isAuthMode">
          <label for="pin-confirm">確認用 (もう一度入力)：</label>
          <input 
            type="password" 
            id="pin-confirm" 
            v-model="confirmPin" 
            maxlength="4" 
            pattern="[0-9]*" 
            inputmode="numeric"
            @input="validateConfirmPin"
          />
          <div v-if="confirmError" class="pin-error">{{ confirmError }}</div>
        </div>
      </div>
      
      <div class="pin-modal-buttons">
        <button class="pin-cancel-button" @click="cancel">キャンセル</button>
        <button 
          class="pin-save-button" 
          @click="save" 
          :disabled="!isValid || isSaving"
        >
          {{ isSaving ? (isAuthMode ? '認証中...' : '保存中...') : (isAuthMode ? '認証' : '保存') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PinCodeModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    isAuthMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      pinCode: '',
      confirmPin: '',
      pinError: '',
      confirmError: '',
      isSaving: false
    }
  },
  computed: {
    isValid() {
      if (this.isAuthMode) {
        // 認証モードでは、PINコードが4桁で入力エラーがなければOK
        return this.pinCode.length === 4 && !this.pinError;
      } else {
        // 登録モードでは、PINコードが4桁で確認用と一致し、エラーがなければOK
        return this.pinCode.length === 4 && this.pinCode === this.confirmPin && !this.pinError;
      }
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        // 親コンポーネントのpendingCloudAuthIdの存在を確認
        const parentApp = this.$parent;
        if (parentApp && parentApp.pendingCloudAuthId && !this.isAuthMode) {
          // 直接親のisAuthModeを変更
          parentApp.isAuthMode = true;
        }
        
        // モーダルが開いたらフォームをリセット
        this.pinCode = '';
        this.confirmPin = '';
        this.pinError = '';
        this.confirmError = '';
        
        // フォーカスを設定
        this.$nextTick(() => {
          if (this.$refs.pinInput) {
            this.$refs.pinInput.focus();
          }
        });
      }
    },
    
    isAuthMode() {
      // isAuthModeの変更を監視
    }
  },
  methods: {
    validatePinCode() {
      // 数字のみかチェック
      if (!/^\d*$/.test(this.pinCode)) {
        this.pinCode = this.pinCode.replace(/[^\d]/g, '');
      }
      
      if (this.pinCode.length === 0) {
        this.pinError = 'PINコードを入力してください';
      } else if (this.pinCode.length !== 4) {
        this.pinError = 'PINコードは4桁の数字で入力してください';
      } else {
        this.pinError = '';
      }
      
      // 確認用との一致チェック（確認用が入力済みの場合）
      if (this.confirmPin && this.pinCode !== this.confirmPin) {
        this.confirmError = 'PINコードが一致しません';
      } else if (this.confirmPin) {
        this.confirmError = '';
      }
    },
    
    validateConfirmPin() {
      // 数字のみかチェック
      if (!/^\d*$/.test(this.confirmPin)) {
        this.confirmPin = this.confirmPin.replace(/[^\d]/g, '');
      }
      
      if (this.confirmPin.length === 0) {
        this.confirmError = '確認用PINコードを入力してください';
      } else if (this.confirmPin !== this.pinCode) {
        this.confirmError = 'PINコードが一致しません';
      } else {
        this.confirmError = '';
      }
    },
    
    save() {
      if (!this.isValid) return;
      
      this.isSaving = true;
      
      // 親コンポーネントに保存イベントを発行
      setTimeout(() => {
        this.$emit('save', this.pinCode);
        this.isSaving = false;
      }, 500);
    },
    
    cancel() {
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
.pin-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1200;
}

.pin-modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 450px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.pin-modal-title {
  color: #333;
  margin-top: 0;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.pin-description {
  margin-bottom: 20px;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #555;
}

.pin-input-container {
  margin-bottom: 15px;
}

.pin-input-container label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #444;
}

.pin-input-container input {
  width: 100%;
  padding: 10px;
  font-size: 1.1rem;
  letter-spacing: 4px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
}

.pin-input-container input:focus {
  border-color: #e9d0ca;
  outline: none;
  box-shadow: 0 0 0 3px rgba(233, 208, 202, 0.25);
}

.pin-error {
  color: #d9534f;
  font-size: 0.85rem;
  margin-top: 4px;
}

.pin-modal-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.pin-cancel-button {
  background-color: #f5f5f5;
  color: #333;
  border: none;
  padding: 10px 18px;
  border-radius: 4px;
  margin-right: 10px;
  cursor: pointer;
  font-weight: 500;
}

.pin-save-button {
  background-color: #e9d0ca;
  color: #333;
  border: none;
  padding: 10px 18px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.pin-save-button:hover {
  background-color: #e0c0b8;
}

.pin-save-button:disabled {
  background-color: #e9d0ca;
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 600px) {
  .pin-modal-content {
    width: 95%;
    padding: 15px;
  }
  
  .pin-input-container input {
    font-size: 1rem;
  }
  
  .pin-cancel-button, .pin-save-button {
    padding: 8px 15px;
  }
}
</style>